import * as React from 'react';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import CollapsibleSection from '@sdk/components/ui/CollapsibleSection';
import Text14 from '@sdk/components/ui/typography/Text14';
import Row from '@sdk/components/ui/spacing/Row';
import gettext, { ngettext } from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import SettingsSection from '../../../settings/components/SettingsSection';
import SettingsSwitcherRow from '../../../settings/components/SettingsSwitcherRow';
import { CustomTextField } from '../components/CustomTextField';
import { FIELD_WIDTH_150 } from '../../../settings/constants';

import type { ClockInEarlyEnabledField, ClockInEarlyMinutesField } from './types/TimeclockFields';

type Props = {
  clockInEarlyEnabledField: ClockInEarlyEnabledField;
  clockInEarlyMinutesField: ClockInEarlyMinutesField;
};

const EarlyClockIn: React.FC<React.PropsWithChildren<Props>> = ({
  clockInEarlyEnabledField,
  clockInEarlyMinutesField,
}) => {
  return (
    <SettingsSection withSeparator>
      <CollapsibleSection
        isActive={clockInEarlyEnabledField.value}
        headerItem={
          <SettingsSwitcherRow
            title={gettext('Early clock in')}
            subtitle={gettext(
              'Allow employees to clock in prior to the scheduled start of the shift'
            )}
            value={clockInEarlyEnabledField.value}
            onChange={(value): void => {
              clockInEarlyEnabledField.onChange(value);
              !value && clockInEarlyMinutesField.dirty ? clockInEarlyMinutesField.reset() : null;
            }}
          />
        }
        contentItem={
          <Row pTop={20} alignItems="center" justifyContent="space-between">
            <Text14 themeColor={ThemeColorType.gray} pRight={10}>
              <TranslateWithComponents
                replaceComponents={{
                  component: (
                    <Text14 themeColor={ThemeColorType.primary}>
                      {clockInEarlyMinutesField.value}
                    </Text14>
                  ),
                }}>
                {ngettext(
                  'Employees can clock in [[component]] minute before shift starts',
                  'Employees can clock in [[component]] minutes before shift starts',
                  Number(clockInEarlyMinutesField.value)
                )}
              </TranslateWithComponents>
            </Text14>

            <CustomTextField
              width={FIELD_WIDTH_150}
              skipErrorMessage
              postfix={gettext('Minutes')}
              value={clockInEarlyMinutesField.value}
              onChange={(event): void =>
                clockInEarlyMinutesField.onChange(Number(event.currentTarget.value) || 0)
              }
              onBlur={clockInEarlyMinutesField.onBlur}
              invalid={!!clockInEarlyMinutesField.error}
            />
          </Row>
        }
      />
    </SettingsSection>
  );
};

export default EarlyClockIn;
